import type { AuditEntry } from '@palaccount/contracts';
import { useTranslation } from 'react-i18next';

import { Ltr } from '@/components/amount';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { fieldLabel } from '@/features/audit/field-label';

/**
 * A document's history card — the shared audit log filtered to ONE record,
 * drawn as SAL-04 §5e's four-part line: timestamp · action · what changed ·
 * who. The bill screen's card (PUR-03 §5e) was the first; wave 5C lifts the
 * same drawing out so the goods receipt can carry one without a second copy
 * of the line format. The six-cap and the newest-first order are the
 * SERVER's promise; this draws what the wire gives it.
 *
 * Two or more changes in one entry join with a middle dot, an empty value
 * draws as an em dash, the arrow is dictionary text (`unit.arrow`) so it
 * points along the reading direction in Arabic (NFR-04), and every field name
 * reads as words in the reader's language (`audit.field.*`, wave 1C).
 *
 * The empty sentence is the CALLER's, because it must name the document
 * (BR-PUR-03-09's lesson: a bill's card that said "order" was a defect).
 */
export function DocumentHistoryCard({
  entries,
  loading,
  emptyText,
}: {
  entries: readonly AuditEntry[];
  loading: boolean;
  emptyText: string;
}) {
  const { t, i18n } = useTranslation();

  const stamp = (iso: string): string =>
    new Intl.DateTimeFormat(i18n.language, {
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(new Date(iso));

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('history.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="h-10 animate-pulse rounded-md bg-muted" aria-busy />
        ) : entries.length === 0 ? (
          <p className="text-sm text-muted-foreground">{emptyText}</p>
        ) : (
          <ul className="grid gap-1.5 text-sm" data-testid="document-history">
            {entries.map((entry) => (
              <li key={entry.id} className="flex items-baseline gap-3">
                <span className="w-24 shrink-0 font-mono text-xs text-muted-foreground">
                  <Ltr value={stamp(entry.occurredAt)} />
                </span>
                <span className="w-[70px] shrink-0 font-semibold">
                  {t(`audit.action.${entry.action}`)}
                </span>
                <span className="min-w-0 flex-1 break-words text-xs">
                  {entry.changes
                    .map(
                      (change) =>
                        `${fieldLabel(t, change.field)}: ${change.from ?? '—'} ${t('unit.arrow')} ${change.to ?? '—'}`,
                    )
                    .join(' · ')}
                </span>
                <span className="shrink-0 text-end text-xs text-muted-foreground">
                  {entry.actorName ?? entry.actorEmail ?? t('audit.systemActor')}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
